import React, { useEffect, useState } from "react";
import { message, Spin } from "antd";
import { useRecoilValue } from "recoil";
import {
  SettingOutlined,
  CloudServerOutlined,
  RocketOutlined,
  ToolOutlined,
  CodeOutlined,
  AppstoreOutlined,
  ApiOutlined,
  BarChartOutlined,
  FundOutlined,
  DatabaseOutlined,
  SafetyCertificateOutlined,
  DownOutlined,
  LockOutlined,
  GitlabOutlined,
} from "@ant-design/icons";
import {
  tenantsInfo as recoilTenantsInfo,
  applicationPermissions as recoilApplicationPermissions,
} from "@/recoil";
import { envConfig } from "@/envConfig";
import { getRefreshToken } from "@/utils/sessionToken";
import InternalAppSection from "./internal-apps/InternalAppSection";
import "./app-home.less";

const { homeUrl } = envConfig;

const REGION = process.env.REACT_APP_REGION;

const EXCHANGE_BASE =
  process.env.REACT_APP_NODE_ENV === "prod"
    ? "https://ldap2gasref.gs-robot.com/exchange"
    : "https://ldap2gasref.test.gs-robot.com/exchange";

const FACTORY_MANAGEMENT_GROUP_LABEL = "工厂管理";

interface AppItem {
  key: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  href: string;
  permission?: string;
  isTest?: boolean;
  isNew?: boolean;
  /** 需要通过 ldap2gasref exchange 换取 token 跳转 */
  useExchange?: boolean;
  exchangeTarget?: string;
}

interface AppGroup {
  label: string;
  subLabel?: string;
  items: AppItem[];
}

interface QuickLink {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  permission?: string;
}

const APP_GROUPS: AppGroup[] = [
  {
    label: "机器人管理",
    // subLabel: "设备管控 · 升级\n远程运维",
    items: [
      {
        key: "parameterFlowManagement",
        title: "机器分析与管控",
        description: "事件分析、地图管理、梯控管理等功能管理平台",
        icon: <SettingOutlined />,
        href: `${homeUrl}/parameter-flow-management`,
        permission: "PTM",
      },
      {
        key: "otaManagement",
        title: "升级管理",
        description: "机器人升级与固件版本管理平台",
        icon: <CloudServerOutlined />,
        href: `${homeUrl}/ota-management`,
        permission: "UpgradeManagement",
      },
      {
        key: "operationMaintenance",
        title: "远程运维",
        description: "远程运维、客服中心等功能管理平台",
        icon: <ToolOutlined />,
        href: `${homeUrl}/operation-maintenance`,
        permission: "OM",
      },
      ...(REGION
        ? []
        : [
            {
              key: "custo",
              title: "智能客服工具",
              description: "智能客服与设备诊断工具",
              icon: <ToolOutlined />,
              href: "https://custo.gs-robot.com",
              useExchange: true,
              exchangeTarget: "custo.gs-robot.com",
            },
            {
              key: "ark",
              title: "工单与售后运营",
              description: "工单派单与售后运营看板",
              icon: <ToolOutlined />,
              href: "https://ark.gs-robot.com",
              useExchange: true,
              exchangeTarget: "ark.gs-robot.com",
            },
          ]),
    ],
  },
  {
    label: "研发效能",
    items: [
      {
        key: "firmwareDevelopmentPlatform",
        title: "版本研发平台",
        description: "分支管理、高级参数、国际化词条等功能的版本研发平台",
        icon: <CodeOutlined />,
        href: `${homeUrl}/firmware-development-platform`,
        permission: "FirmwareDevelopmentPlatform",
      },
      {
        key: "skillHub",
        title: "Skill Hub",
        description: "Skill技能统一管理平台",
        icon: <RocketOutlined />,
        href: "https://skillhub.gs-robot.com",
        isNew: true,
        useExchange: true,
        exchangeTarget: "skillhub.gs-robot.com",
      },
      {
        key: "modelHub",
        title: "Model Hub",
        description: "Model模型统一管理平台",
        icon: <AppstoreOutlined />,
        href: "https://modelhub.gs-robot.com",
        isNew: true,
        useExchange: true,
        exchangeTarget: "modelhub.gs-robot.com",
      },
      {
        key: "apiPlatform",
        title: "接口平台",
        description: "yapi接口文档平台（仅内网访问）",
        icon: <ApiOutlined />,
        href: "https://grod.gs-robot.com",
        isNew: true,
        useExchange: true,
        exchangeTarget: "grod.gs-robot.com",
      },
      {
        key: "codeReview",
        title: "CodeReview",
        description: "代码审查平台",
        icon: <GitlabOutlined />,
        href: "http://8.153.90.1/",
        isTest: true,
        useExchange: false,
        exchangeTarget: "",
      },
    ],
  },
  {
    label: FACTORY_MANAGEMENT_GROUP_LABEL,
    subLabel: "生产制造",
    items: [
      {
        key: "factoryManagement",
        title: "工厂信息管理",
        description: "工厂跑测、设备档案等功能管理平台",
        icon: <DatabaseOutlined />,
        href: `${homeUrl}/factory-management`,
        permission: "FactoryInfoManagement",
      },
    ],
  },
];

const BASIC_CONFIG_GROUP: AppGroup = {
  label: "基础配置",
  items: [
    {
      key: "iamManage",
      title: "IAM 权限管理",
      description: "租户、账号、角色与应用权限统一管理",
      icon: <SafetyCertificateOutlined />,
      href: `${homeUrl}/tenant-center#/gas/tenant/manage`,
    },
    {
      key: "dataCenterManage",
      title: "数据中心管理",
      description: "数据中心后台配置、报表门户资源管理",
      icon: <FundOutlined />,
      href: `${homeUrl}/partner-report`,
    },
    {
      key: "iot",
      title: "物联网平台",
      description: "设备接入、物联数据与连接能力管理",
      icon: <CloudServerOutlined />,
      href: `${homeUrl}/iot`,
    },
    {
      key: "basicDataManage",
      title: "基础数据管理",
      description: "beep基础数据管理",
      icon: <DatabaseOutlined />,
      href: `${homeUrl}/beep`,
    },
    {
      key: "cleanLibrary",
      title: "标准清洁项库",
      description: "标准工作项库管理与配置",
      icon: <DatabaseOutlined />,
      href: `${homeUrl}/clean-library`,
    },
  ],
};

const QUICK_LINKS: QuickLink[] = [
  {
    key: "workbench",
    title: "工作台",
    description: "机器分析管理工作台",
    icon: <SettingOutlined />,
    href: `${homeUrl}/parameter-flow-management/#/workbench`,
    permission: "PTM",
  },
  {
    key: "reportCentre",
    title: "报表中心",
    description: "多指标维度数据看板",
    icon: <BarChartOutlined />,
    href: `${homeUrl}/report-centre`,
    permission: "ReportCentre",
  },
];

const APPHome: React.FC = () => {
  const tenantsInfo = useRecoilValue(recoilTenantsInfo);
  const applicationPermissions = useRecoilValue(recoilApplicationPermissions);
  const [loading, setLoading] = useState(true);
  const [basicExpanded, setBasicExpanded] = useState(false);

  useEffect(() => {
    if (tenantsInfo?.internalPerson !== undefined) {
      setLoading(false);
    }
  }, [tenantsInfo?.internalPerson]);

  const exchangeAndRedirect = async (target: string, fallbackHref: string) => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      message.error("登录已过期，请重新登录");
      return;
    }

    const newWindow = window.open("about:blank", "_blank");

    setLoading(true);
    try {
      const exchangeUrl = `${EXCHANGE_BASE}?token=${encodeURIComponent(
        refreshToken
      )}&default_return_to=${encodeURIComponent(fallbackHref)}`;
      await fetch(exchangeUrl, { mode: "no-cors", credentials: "include" });
      if (newWindow) {
        newWindow.location.href = fallbackHref;
      }
    } catch {
      if (newWindow) {
        newWindow.close();
      }
      message.error("跳转失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  const canAccess = (permission?: string) =>
    !permission || applicationPermissions?.includes(permission);

  const handleAppClick = (item: AppItem) => {
    if (!canAccess(item.permission)) {
      message.error("权限未开通，如需访问请联系管理员");
      return;
    }
    if (item.useExchange && item.exchangeTarget) {
      exchangeAndRedirect(item.exchangeTarget, item.href);
      return;
    }
    window.location.href = item.href;
  };

  const handleQuickLinkClick = (link: QuickLink) => {
    if (!canAccess(link.permission)) {
      message.error("权限未开通，如需访问请联系管理员");
      return;
    }
    window.location.href = link.href;
  };

  const renderAppCard = (item: AppItem, onClick?: () => void) => (
    <button
      key={item.key}
      type="button"
      className={`portal-card${
        !canAccess(item.permission) ? " portal-card--locked" : ""
      }`}
      onClick={onClick ?? (() => handleAppClick(item))}
    >
      <div className="portal-card-icon">{item.icon}</div>
      <div className="portal-card-body">
        <div className="portal-card-title">
          {item.title}
          {item.isNew && <span className="portal-new-tag">NEW</span>}
          {item.isTest && <span className="portal-test-tag">内测</span>}
        </div>
        <div className="portal-card-desc">{item.description}</div>
      </div>
      {!canAccess(item.permission) && (
        <LockOutlined className="portal-card-lock" />
      )}
    </button>
  );

  if (REGION) {
    return (
      <div className="portal-root portal-root--simple">
        <Spin spinning={loading} wrapperClassName="portal-spin">
          <div className="portal-shell">
            <section className="portal-hero portal-hero--compact">
              <div className="portal-hero-main">
                <div className="portal-kicker">
                  <AppstoreOutlined />
                  <span>Application Center</span>
                </div>
                <h1>运维运营工作台</h1>
                <p>集中访问设备管控、升级管理与线上运维入口。</p>
              </div>
            </section>
            <div className="portal-simple-grid">
              {APP_GROUPS[0].items.map((item) => renderAppCard(item))}
            </div>
          </div>
        </Spin>
      </div>
    );
  }

  return (
    <div className="portal-root">
      <Spin spinning={loading} wrapperClassName="portal-spin">
        <div className="portal-shell">
          <section className="portal-hero">
            <div className="portal-hero-main">
              <h1>高仙内部应用</h1>
              <p>
                您已进入公司内网，为保障信息安全，请勿随意分享页面或截图，感谢配合！
              </p>
            </div>
          </section>

          <div className="portal-body">
            <main className="portal-main">
              <div className="portal-group-list">
                {APP_GROUPS.map((group) => (
                  <React.Fragment key={group.label}>
                    {group.label === FACTORY_MANAGEMENT_GROUP_LABEL && (
                      <InternalAppSection renderAppCard={renderAppCard} />
                    )}
                    <section className="portal-section">
                      <div className="portal-section-header">
                        <div className="portal-section-label">
                          <span className="portal-section-title">
                            {group.label}
                          </span>
                        </div>
                      </div>
                      <div className="portal-section-cards">
                        {group.items.map((item) => renderAppCard(item))}
                      </div>
                    </section>
                  </React.Fragment>
                ))}

                {/* 基础配置 - 折叠区块 */}
                <section className="portal-section portal-section--collapsible">
                  <div
                    className="portal-collapse-header"
                    onClick={() => setBasicExpanded((v) => !v)}
                  >
                    <span className="portal-section-title">
                      {BASIC_CONFIG_GROUP.label}
                    </span>
                    <DownOutlined
                      className={`portal-collapse-arrow${
                        basicExpanded ? " portal-collapse-arrow--open" : ""
                      }`}
                    />
                  </div>
                  {basicExpanded && (
                    <div className="portal-section-cards portal-section-cards--muted">
                      {BASIC_CONFIG_GROUP.items.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          className={`portal-card portal-card--basic${
                            !canAccess(item.permission)
                              ? " portal-card--locked"
                              : ""
                          }`}
                          onClick={() => handleAppClick(item)}
                        >
                          <div className="portal-card-icon">{item.icon}</div>
                          <div className="portal-card-body">
                            <div className="portal-card-title">
                              {item.title}
                            </div>
                            <div className="portal-card-desc">
                              {item.description}
                            </div>
                          </div>
                          {!canAccess(item.permission) && (
                            <LockOutlined className="portal-card-lock" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </main>

            <aside className="portal-sidebar">
              <section className="portal-panel">
                <div className="portal-panel-header">
                  <div>
                    <h2>通用工具</h2>
                  </div>
                  <ToolOutlined className="portal-panel-icon" />
                </div>
                <div className="portal-quick-links">
                  {QUICK_LINKS.map((link) => (
                    <button
                      key={link.key}
                      type="button"
                      className={`portal-quick-item${
                        !canAccess(link.permission)
                          ? " portal-quick-item--locked"
                          : ""
                      }`}
                      onClick={() => handleQuickLinkClick(link)}
                    >
                      <div className="portal-quick-icon">{link.icon}</div>
                      <div className="portal-quick-body">
                        <div className="portal-quick-title">
                          {link.title}
                          {!canAccess(link.permission) && (
                            <LockOutlined className="portal-quick-lock" />
                          )}
                        </div>
                        <div className="portal-quick-desc">
                          {link.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default APPHome;
